/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: AI Assistant
 🏢 Company: Ring2All LLC
 📄 File: ModuleFormHeader.tsx
 🗓️ Created: 2025-01-18
 🧩 Description:
    Reusable header component for module form pages.
    Displays back button, title, icon, and optional actions.

 🔧 Usage:
    <ModuleFormHeader
      icon={<Shield className="h-4 w-4 text-primary" />}
      title={t('module.edit')}
      backButtonText={t('module.backToList')}
      onBack={() => handleBack()}
      actions={[...custom actions...]}
    />

 🔁 Last Updated: 2025-01-18 by AI Assistant
────────────────────────────────────────────────────────────────*/

import { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useSidebarState } from '../../hooks/useSidebarState';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';

interface FormAction {
  icon: ReactNode;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
}

interface ModuleFormHeaderProps {
  icon: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
  backButtonText: string;
  backButtonIcon?: ReactNode;
  hideBackButton?: boolean;
  onBack: () => void;
  actions?: FormAction[];
  searchInput?: ReactNode;
  children?: ReactNode;
}

export function ModuleFormHeader({
  icon,
  title,
  subtitle,
  description,
  backButtonText,
  backButtonIcon,
  hideBackButton = false,
  onBack,
  actions = [],
  searchInput,
  children
}: ModuleFormHeaderProps) {
  const { isOpen: isSidebarOpen, width: sidebarWidth } = useSidebarState();
  const effectiveSidebarWidth = sidebarWidth === 0 ? 350 : sidebarWidth;

  return (
    <>
      <div
        className="bg-muted/75 backdrop-blur-md border border-border/50 rounded-lg p-3 fixed top-[80px] z-40 shadow-lg"
        style={{
          left: `${effectiveSidebarWidth + 1}px`,
          right: '1px'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!hideBackButton && (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-colors border border-primary/20"
              >
                {backButtonIcon ?? <ArrowLeft className="h-4 w-4" />}
                {backButtonText}
              </button>
            )}

            <div className="flex items-center gap-2">
              <div className="p-1 bg-primary/10 rounded-md">
                {icon}
              </div>
              {title && (
                <div className="flex flex-col">
                  <h2 className="text-xl font-semibold text-foreground">
                    {title}
                  </h2>
                  {subtitle ?? description ? (
                    <p className="text-sm text-muted-foreground">
                      {subtitle ?? description}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-1 justify-end">
            {searchInput && (
              <div className="flex-1 max-w-md">
                {searchInput}
              </div>
            )}

            {/* Custom Children (e.g. Save Button) */}
            {children}

            {actions.length > 0 && (
              <div className="flex items-center gap-2">
                {actions.map((action, index) => (
                  <SimpleTooltip key={index} content={action.tooltip}>
                    <button
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={`p-2 rounded-md transition-colors ${action.disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                    >
                      {action.icon}
                    </button>
                  </SimpleTooltip>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Spacer to prevent content from going under fixed header */}
      <div className="h-[10px]"></div>
    </>
  );
}

export default ModuleFormHeader;

